import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Heart, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function LoginPage() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Entre ton mot de passe");
      return;
    }

    setLoading(true);
    try {
      await login(password);
      toast.success("Bienvenue Emma !");
      navigate("/");
    } catch (error) {
      toast.error("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFCFB] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl p-8 shadow-soft">
          {/* Logo / Welcome */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#A3E4D7] to-[#AED6F1] flex items-center justify-center"
            >
              <Heart className="text-white" size={36} />
            </motion.div>
            
            <h1 className="font-nunito text-3xl font-bold text-[#2C3E50] mb-2">
              Bonjour Emma
            </h1>
            <p className="text-[#7F8C8D] text-base leading-relaxed">
              Ton espace de bien-être t'attend
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#7F8C8D]" size={20} />
              <Input
                type="password"
                placeholder="Ton mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="pl-12 h-14 rounded-2xl border-2 border-[#A3E4D7]/30 focus:border-[#A3E4D7] bg-[#FAFCFB] text-[#2C3E50] placeholder:text-[#7F8C8D]"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-14 rounded-full bg-gradient-to-r from-[#A3E4D7] to-[#AED6F1] hover:opacity-90 text-[#2C3E50] font-semibold text-lg transition-all duration-200 pill-button"
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-6 h-6 border-2 border-[#2C3E50]/30 border-t-[#2C3E50] rounded-full"
                />
              ) : (
                "Entrer"
              )}
            </Button>
          </form>

          {/* Hint */}
          <p className="text-center text-[#7F8C8D] text-sm mt-6">
            Ton petit coin de sérénité
          </p>
        </div>

        {/* Decorative elements */}
        <div className="flex justify-center gap-2 mt-6">
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0 }}
            className="w-3 h-3 rounded-full bg-[#A3E4D7]"
          />
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
            className="w-3 h-3 rounded-full bg-[#AED6F1]"
          />
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
            className="w-3 h-3 rounded-full bg-[#FAD7A1]"
          />
        </div>
      </motion.div>
    </div>
  );
}
